import { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { useLocalStorageState } from '@components/component-helpers';
import { ActionFn, ColumnSettings, ColumnSort, Columns, DataGrid } from '@components/data-grid';
import { IconCheckmark } from '@components/icons/checkmark';
import { useToaster } from '@components/toaster';
import { sortByObjectKey } from '@utils/collections';

import { api } from 'api';

import { useTasklistTemplates } from 'api/templates/requests';
import { Loading } from 'components/Loading';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useStore } from 'models/Provider';
import { TasklistTemplate } from 'types/templates';

export const TasklistTemplates = observer(function TasklistTemplates() {
  const { t } = useStore();
  const { showSuccess, showError } = useToaster();
  const { showModal } = useModal();
  const { hasRolePermission } = usePermissions();
  const listQuery = useTasklistTemplates();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const columns = useRef<Columns<TasklistTemplate>>([
    {
      label: t('company.templates.tasklist.label.name'),
      key: 'name',
      sortable: true,
      type: 'text-long',
    },
    {
      label: 'Tasks',
      labelIcon: IconCheckmark,
      key: 'items',
      type: 'text-short',
      center: true,
      render: (template) => <span>{template.items.length}</span>,
    },
  ]).current;

  const [sort, setSort] = useLocalStorageState<ColumnSort<TasklistTemplate>>({
    key: 'tasklist-template-sort',
    defaultValue: {
      key: 'name',
      order: 'ascending',
    },
  });

  const [settings, setSettings] = useLocalStorageState<ColumnSettings<TasklistTemplate>>({
    key: 'tasklist-templates-columns',
    defaultValue: {
      name: { order: 0, visible: true },
      items: { order: 1, visible: true },
    },
  });

  const deleteMutation = useMutation((id: number) => api.templates.deleteTasklistTemplate(id), {
    onSuccess() {
      showSuccess(t('templates.store.removetasklist.success'));
      queryClient.invalidateQueries(['tasklist-templates']);
    },
    onError() {
      showError(t('templates.store.removetasklist.error'));
    },
  });

  const removeTasklistModal = useCallback(
    (item: TasklistTemplate) => {
      showModal('COMMON_REMOVE_CONFIRM', {
        _container: {
          autoWidth: true,
        },
        data: { id: item.id, confirmText: t('modals.removeconfirm.tasklist.confirm') },
        async confirm() {
          try {
            await deleteMutation.mutateAsync(item.id);
            return true;
          } catch (e) {
            return false;
          }
        },
      });
    },
    [deleteMutation, showModal, t]
  );

  const actionsForRow = useCallback<ActionFn<TasklistTemplate>>(
    (template) => {
      return [
        {
          label: t('company.templates.options.delete'),
          onSelect: () => removeTasklistModal(template),
          variant: 'danger',
        },
      ];
    },
    [removeTasklistModal, t]
  );

  const sortedTemplates = useMemo(() => {
    if (!listQuery.data) return [];

    return sortByObjectKey(
      listQuery.data,
      'name',
      sort.order === 'descending'
    ) as Array<TasklistTemplate>;
  }, [listQuery.data, sort.order]);

  if (!listQuery.data) return <Loading delay={500} />;

  return (
    <DataGrid<TasklistTemplate>
      label="Tasklist templates"
      sort={sort}
      onSortChange={setSort}
      columnSettings={settings}
      onColumnSettingsChange={setSettings}
      columns={columns}
      data={sortedTemplates}
      actions={!hasRolePermission('company:edit-templates') ? undefined : actionsForRow}
      onRowClick={{
        label: t('company.templates.tasklist.menu.edit'),
        callback: (template) => {
          navigate(`/settings/templates/tasks/${template.id}`);
        },
      }}
    />
  );
});
